import { AmountDisplay } from "@/app/shared/ui/amount-display";
import { AITransactionFilter } from "@/components/AITransactionFilter";
import { CreateTransactionSheet } from "@/components/CreateTransactionSheet";
import { UpdateTransactionSheet } from "@/components/UpdateTransactionSheet";
import { InlineTransactionTable } from "@/components/InlineTransactionTable";
import { TransactionFilters } from "@/components/TransactionFilters";
import { TransactionSortSelector } from "@/components/TransactionSortSelector";
import { Button } from "@groot/ui/button";
import { Input } from "@groot/ui/input";
import { DateRangePicker, type DateRange } from "@/app/shared/ui/date-range-picker";
import { Pagination } from "@/app/shared/ui/pagination";
import { useAccounts, useCategories, useTags, useTransaction, useTransactions } from "@/hooks/api";
import { endOfMonthFormatted, formatDate, startOfMonthFormatted } from "@/lib/date.utils";
import type { TransactionFilters as FilterType, TransactionType } from "@/types";
import dayjs from "dayjs";
import { Plus, Search, X } from "lucide-react";
import { parseAsInteger, parseAsString, useQueryStates } from "nuqs";
import { useEffect, useState } from "react";

export function Transactions() {
  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const [isAddingInline, setIsAddingInline] = useState(false);

  // Use nuqs for URL query state management
  const [queryParams, setQueryParams] = useQueryStates({
    id: parseAsInteger,
    accountId: parseAsInteger,
    startDate: parseAsString,
    endDate: parseAsString,
    categoryId: parseAsInteger,
    type: parseAsString,
    isReviewed: parseAsString,
    minAmount: parseAsInteger,
    maxAmount: parseAsInteger,
    notes: parseAsString,
    search: parseAsString,
    sortBy: parseAsString.withDefault("date"),
    sortOrder: parseAsString.withDefault("desc"),
    page: parseAsInteger.withDefault(1),
  });

  // Build filters from URL query params
  const PAGE_SIZE = 50;
  const currentPage = queryParams.page || 1;

  const filters: FilterType = {
    startDate: queryParams.startDate || startOfMonthFormatted(),
    endDate: queryParams.endDate || endOfMonthFormatted(),
    accountId: queryParams.accountId || undefined,
    categoryId: queryParams.categoryId || undefined,
    type: (queryParams.type as TransactionType) || undefined,
    isReviewed:
      queryParams.isReviewed === "true"
        ? true
        : queryParams.isReviewed === "false"
          ? false
          : undefined,
    minAmount: queryParams.minAmount || undefined,
    maxAmount: queryParams.maxAmount || undefined,
    search: queryParams.search || undefined,
    limit: PAGE_SIZE,
    offset: (currentPage - 1) * PAGE_SIZE,
    sortBy: (queryParams.sortBy as "date" | "amount" | "createdAt") || "date",
    sortOrder: (queryParams.sortOrder as "asc" | "desc") || "desc",
  };

  // React Query hooks - data is automatically cached and refetched
  const { data: transactionsData, isLoading } = useTransactions(filters);
  const { data: accounts = [] } = useAccounts();
  const { data: categories = [] } = useCategories();
  const { data: tags = [] } = useTags();

  const { data: transactionDetail } = useTransaction(queryParams.id || 0);

  const transactions = transactionsData?.transactions || [];
  const total = transactionsData?.total || 0;
  const selectedAccount = queryParams.accountId
    ? accounts.find((acc) => acc.id === queryParams.accountId)
    : null;
  const activeAccounts = accounts.filter((account) => !account.isArchived && !account.isDeleted);

  // Pagination calculations
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleFiltersChange = (newFilters: FilterType) => {
    // Update URL query params and reset to page 1
    setQueryParams({
      accountId: newFilters.accountId || null,
      startDate: newFilters.startDate || null,
      endDate: newFilters.endDate || null,
      categoryId: newFilters.categoryId || null,
      type: newFilters.type || null,
      isReviewed: newFilters.isReviewed !== undefined ? String(newFilters.isReviewed) : null,
      minAmount: newFilters.minAmount || null,
      maxAmount: newFilters.maxAmount || null,
      notes: queryParams.notes || null,
      page: 1,
    });
  };

  const handleDateRangeChange = (dateRange?: DateRange) => {
    if (!dateRange?.from || !dateRange?.to) return;

    setQueryParams({
      startDate: formatDate(dateRange.from),
      endDate: formatDate(dateRange.to),
      page: 1,
    });
  };

  const handlePageChange = (newPage: number) => {
    setQueryParams({ page: newPage });
  };

  const handleSortChange = (newSortOrder: "asc" | "desc") => {
    setQueryParams({ sortOrder: newSortOrder, page: 1 });
  };

  const handleColumnSort = (sortBy: "date" | "amount", sortOrder: "asc" | "desc") => {
    setQueryParams({ sortBy, sortOrder, page: 1 });
  };

  // Actual-style search box (top-right), debounced into the `search` query param
  const [searchInput, setSearchInput] = useState(queryParams.search || "");

  useEffect(() => {
    setSearchInput(queryParams.search || "");
  }, [queryParams.search]);

  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === (queryParams.search || "")) return;

    const timeout = setTimeout(() => {
      setQueryParams({ search: trimmed || null, page: 1 });
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchInput, queryParams.search, setQueryParams]);

  const handleAddNew = () => {
    // Desktop: inline spreadsheet-style add row; mobile: full create sheet
    if (window.matchMedia("(min-width: 768px)").matches) {
      setIsAddingInline(true);
    } else {
      setIsCreateSheetOpen(true);
    }
  };

  return (
    // Full viewport height minus the shell's mobile top bar (h-14 = 3.5rem,
    // hidden on lg+) — keeps the pinned pagination footer flush with the bottom
    // of the viewport instead of leaving a dead band below it.
    <div className="h-[calc(100vh-3.5rem)] lg:h-screen bg-background">
      {/* Main Content */}
      <div className="h-full flex flex-col overflow-hidden">
        {/* Header Bar */}
        <div className="px-4 md:px-6 py-4 border-b border-border">
          <div className="flex items-center justify-between mb-3 gap-2">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {selectedAccount ? (
                <div className="flex items-center gap-3 min-w-0">
                  {selectedAccount.iconUrl && (
                    <img
                      src={selectedAccount.iconUrl}
                      alt={selectedAccount.accountName}
                      className="size-6 rounded flex-shrink-0"
                    />
                  )}
                  <div className="min-w-0">
                    <h2 className="text-lg font-medium text-foreground truncate">
                      {selectedAccount.accountName}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      <AmountDisplay
                        amount={selectedAccount.currentBalance || 0}
                        currencyCode={selectedAccount.currencyCode || "INR"}
                      />
                    </p>
                  </div>
                </div>
              ) : (
                <h2 className="text-lg font-medium text-foreground">All Transactions</h2>
              )}
            </div>

            {/* Actual-style search box */}
            <div className="relative flex-shrink-0 w-40 sm:w-56">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search"
                className="h-8 pl-8 pr-7 text-sm"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => setSearchInput("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Clear search"
                >
                  <X className="size-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Toolbar: Add New, Filter, sort, date range (Actual-style) */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:flex-wrap">
            <Button size="sm" variant="outline" className="gap-1.5 h-7" onClick={handleAddNew}>
              <Plus className="size-3.5" />
              Add New
            </Button>
            <TransactionFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
              accounts={activeAccounts}
              categories={categories}
            />
            <div className="flex gap-2 w-full sm:w-auto sm:ml-auto">
              <div className="md:hidden">
                <TransactionSortSelector
                  value={filters.sortOrder || "desc"}
                  onValueChange={handleSortChange}
                />
              </div>
              <AITransactionFilter
                onFilterApply={handleFiltersChange}
                accounts={activeAccounts}
                categories={categories}
              />
              <DateRangePicker
                className="w-full sm:w-auto"
                value={{
                  from: filters.startDate ? dayjs(filters.startDate).toDate() : undefined,
                  to: filters.endDate ? dayjs(filters.endDate).toDate() : undefined,
                }}
                onChange={handleDateRangeChange}
              />
            </div>
          </div>
        </div>

        {/* Transaction Table */}
        <div className="flex-1 overflow-auto">
          <InlineTransactionTable
            transactions={transactions}
            accounts={activeAccounts}
            categories={categories}
            tags={tags}
            isLoading={isLoading}
            onEdit={(transaction) => setQueryParams({ id: transaction.id })}
            startDate={filters.startDate}
            endDate={filters.endDate}
            sortBy={filters.sortBy}
            sortOrder={filters.sortOrder}
            onSort={handleColumnSort}
            isAdding={isAddingInline}
            onCancelAdd={() => setIsAddingInline(false)}
            defaultAccountId={queryParams.accountId || undefined}
          />
        </div>

        {/* Pagination Controls */}
        {total > PAGE_SIZE && (
          <div className="px-4 md:px-6 py-3 border-t border-border">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={total}
              itemsPerPage={PAGE_SIZE}
              onPageChange={handlePageChange}
              isLoading={isLoading}
              itemName="transactions"
            />
          </div>
        )}
      </div>

      {/* Create Transaction Sheet */}
      <CreateTransactionSheet
        accounts={activeAccounts}
        categories={categories}
        tags={tags}
        isOpen={isCreateSheetOpen}
        onOpenChange={setIsCreateSheetOpen}
        defaultAccountId={queryParams.accountId || undefined}
      />

      {/* Edit Transaction Sheet */}
      <UpdateTransactionSheet
        transaction={transactionDetail || null}
        accounts={accounts}
        categories={categories}
        tags={tags}
        isOpen={!!queryParams.id}
        onOpenChange={(open) => {
          if (!open) setQueryParams({ id: null });
        }}
        onUpdate={() => {
          setQueryParams({ id: null });
        }}
      />
    </div>
  );
}
